import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { DispatchOrder } from "../../api/dispatchOrders";
import { readable } from "../CompanyErpStatus";

/**
 * Where a dispatch order stands on its way into the ERP, where it becomes the loading order the warehouse loads by:
 * written with the ERP's number, waiting (and for what), or not taken, with the reason. Nothing before quality
 * released it whole. `compact` is a line in a list; otherwise a box under the order.
 */
export function DispatchErpStatus({ order, compact = false }: { order: DispatchOrder; compact?: boolean }) {
  const { t } = useTranslation();
  const error = readable(order.erpError);
  let text: string;
  let tone: CSSProperties;
  if (order.erpDocumentNumber) {
    // U ERP-u nalog nosi ERP-ov broj, pa se broj ne ponavlja; kaže se samo koji je broj imao u portalu.
    text =
      order.erpDocumentNumber !== order.number
        ? t("dispatchOrders.erp.sent", "in the ERP as loading order {{number}}", { number: order.erpDocumentNumber })
        : order.portalNumber
          ? t("dispatchOrders.erp.sentAs", "in the ERP as this loading order; in the portal it was {{portal}}", {
              portal: order.portalNumber,
            })
          : t("dispatchOrders.erp.sentSame", "in the ERP as this loading order");
    tone = ok;
  } else if (order.erpStatus === "FAILED") {
    text = t("dispatchOrders.erp.failed", "the ERP did not take it: {{error}}", { error });
    tone = bad;
  } else if (order.erpStatus === "PENDING" && order.erpError) {
    text = t("dispatchOrders.erp.waiting", "waiting to go to the ERP: {{error}}", { error });
    tone = warn;
  } else if (order.erpStatus === "PENDING") {
    text = t("dispatchOrders.erp.pending", "waiting to go to the ERP");
    tone = muted;
  } else {
    return null;
  }
  if (compact) return <span style={{ fontSize: 12, color: tone.color }}>{text}</span>;
  return (
    <div style={{ ...box, ...tone }} data-testid="dispatch-erp-status">
      <strong>{t("dispatchOrders.erp.label", "ERP")}:</strong> {text}
      {order.erpStatus === "FAILED" && (
        <div style={{ marginTop: 4, color: "var(--ink-3)" }}>
          {t("admin.companyDetail.erp.failedHint", "After the fix, retry it with “Retry” on the ERP outbox screen.")}
        </div>
      )}
    </div>
  );
}

const box: CSSProperties = { padding: "8px 12px", borderRadius: 8, fontSize: 13, border: "1px solid var(--border)" };
const ok: CSSProperties = { background: "var(--ok-bg)", color: "var(--ok)" };
const bad: CSSProperties = { background: "var(--err-bg)", color: "var(--err)" };
const warn: CSSProperties = { background: "var(--warn-bg)", color: "var(--warn)" };
const muted: CSSProperties = { background: "var(--surface-2)", color: "var(--ink-2)" };
